import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AgentMode, Conversation } from '~api';
import { AgentManager, AgentManagerConfig } from '~portal/agents';
import { api } from '~portal/api';
import { logger } from '~portal/lib';
import { ConversationStore, useConversationStore } from '~portal/stores';

function useAgentManager(config?: AgentManagerConfig) {
  let manager = useConversationStore((state) => state.agentManager);
  if (manager) return manager;

  // create a new manager instance if missing
  manager = new AgentManager(config);
  useConversationStore.setState((state) => {
    state.agentManager = manager;
  });

  logger.debug('[ConversationStore] AgentManager initialized');
  return manager;
}

/**
 * @desc Hook to manage conversation lifecycle and provide messaging interface.
 * Activates the conversation agent on mount and deactivates on unmount.
 * Subscribes to promotion events to handle navigation when 'new' becomes real ID.
 */
export function useConversation(conversationId: string, mode?: AgentMode.T) {
  const navigate = useNavigate();
  const trpc = api.useUtils();
  const manager = useAgentManager();
  const agent = manager.getAgent(conversationId);

  // Get history and grouped view from store
  const isNew = conversationId === 'new';
  const storeId = agent.storeId ?? conversationId;

  // Use selectors to subscribe only to the specific conversation slice
  // IMPORTANT: Don't create new references inside selectors (e.g., ?? []) to avoid infinite loops
  const context = useConversationStore((state) => state.contexts[storeId]);
  const groups = useConversationStore((state) => state.grouped[storeId]) ?? [];

  // Get latest narration for loading indicator
  const lastGroup = groups.length > 0 ? groups[groups.length - 1] : undefined;
  const latestTimeline = lastGroup?.timeline;
  const latestNarrationGroup =
    latestTimeline && latestTimeline.length > 0
      ? latestTimeline[latestTimeline.length - 1]
      : undefined;
  const latestNarration = latestNarrationGroup?.narration;

  // Send message function
  const sendMessage = (message: string) =>
    agent.sendMessage(trpc, message, mode, async (newId: string) =>
      navigate({
        to: '/chat/$conversationId',
        params: { conversationId: newId },
      }),
    );

  // Client-side data fetching: if not a new conversation and no history, run query
  const shouldFetchData = !isNew && context === undefined;

  // Fetch conversation data from server (first load only)
  const { data, isLoading: isDataLoading } = api.conversations.get.useQuery(
    { conversationId },
    {
      enabled: shouldFetchData,
      retry: true,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 0, // Data is always stale, will refetch on mount after HMR
      refetchOnMount: false, // Always refetch on mount since staleTime=0
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  );

  // Sync fetched data to store (including when store resets and shouldFetchData changes)
  useEffect(() => {
    if (data) ConversationStore.setConversation(data);
  }, [data, shouldFetchData]);

  // Attach to a possibly-in-flight run (H2-172): the turn survives reloads and navigation, so
  // on loading an existing conversation ask the server whether one is live — if so the socket
  // stays open and its events stream into the store; if not, this is a no-op.
  useEffect(() => {
    if (isNew || !data) return;
    agent.resume().catch((err: unknown) => {
      logger.error('[useConversation] resume failed', {
        conversationId,
        error: err instanceof Error ? err.message : String(err),
      });
    });
  }, [agent, conversationId, isNew, data]);

  // Determine display state
  // Loading until groups is populated (for existing conversations)
  const isLoadingState = !isNew && groups.length === 0 && (isDataLoading || context === undefined);
  const isEmptyState = !context?.length && !isDataLoading;
  const isSubmitting = useConversationStore((state) => state.activeAgents.has(conversationId));

  // query helpers
  const find = {
    toolResult: (toolUseId: string): Conversation.ClientEvent | undefined => {
      // Check for ToolOutput events (current system)
      return context?.find(
        (evt) => evt.name === 'ToolOutput' && evt.payload.toolUseId === toolUseId,
      );
    },
  };

  return {
    sendMessage,
    conversation: context,
    groups, // Grouped view for new UI
    latestNarration, // For loading indicator
    isDataLoading,
    isLoadingState,
    isEmptyState,
    isSubmitting,
    find,
  };
}

export type UseConversation = ReturnType<typeof useConversation>;
export type UseConversationFind = UseConversation['find'];
